import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreInitialize, useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { getIsValidAuthenticationCode, getIsValidEmail, getIsValidPassword, getIsValidUsername } from '@util/string';
import {
  useAuthenticateCodeMutation,
  useFindMeQuery,
  useInhouseSigninMutation,
  useInhouseSignupMutation,
  useIssueCodeMutation,
  useModifyMeMutation,
  useOAuthSignupMutation,
  useOpenOAuthSignInPageMutation,
  useRemoveMeMutation,
  useRepasswordMutation,
  useSignoutMutation,
} from '@hook/auth/useApi';
import { EService, EAuthType } from '@datatype/type';
import { actions as authActions } from '@store/slice/auth-slice';
import { actions as userActions } from '@store/slice/user-slice';
import { actions as navigationActions } from '@store/slice/navigation-slice';
import { actions as modalActions } from '@store/slice/modal-slice';
import { SERVICE } from '@config/environment';
import { LOCAL_STORAGE_STUDENT_KEY } from '@config/auth';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { getInitialServicePath } from '@constant/common/route-path';

export const useTerm = () => {
  const dispatch = useTypedDispatch();

  const [isTotalTerm, setIsTotalTerm] = useState<boolean>(false);
  const [isAdultTerm, setIsAdultTerm] = useState<boolean>(false);
  const [isServiceTerm, setIsServiceTerm] = useState<boolean>(false);
  const [isInformationTerm, setIsInformationTerm] = useState<boolean>(false);
  const isMarketingTerm = useTypedSelector((state) => state.rootReducer.authReducer.isMarketingTerm);

  const [isServiceTermDescription, setIsServiceTermDescription] = useState<boolean>(false);
  const [isInformationTermDescription, setIsInformationTermDescription] = useState<boolean>(false);
  const [isMarketingTermDescription, setIsMarketingTermDescription] = useState<boolean>(false);

  const handleTotalTermClick = () => {
    setIsTotalTerm(!isTotalTerm);
    setIsAdultTerm(!isTotalTerm);
    setIsServiceTerm(!isTotalTerm);
    setIsInformationTerm(!isTotalTerm);
    dispatch(authActions.checkMarketingTerm(!isTotalTerm));
  };

  const handleAdultTermClick = () => {
    setIsAdultTerm(!isAdultTerm);
    setIsTotalTerm(!isAdultTerm && isServiceTerm && isInformationTerm && isMarketingTerm);
  };

  const handleServiceTermClick = () => {
    setIsServiceTerm(!isServiceTerm);
    setIsTotalTerm(isAdultTerm && !isServiceTerm && isInformationTerm && isMarketingTerm);
  };

  const handleInformationTermClick = () => {
    setIsInformationTerm(!isInformationTerm);
    setIsTotalTerm(isAdultTerm && isServiceTerm && !isInformationTerm && isMarketingTerm);
  };

  const handleMarketingTermClick = () => {
    dispatch(authActions.checkMarketingTerm(!isMarketingTerm));
    setIsTotalTerm(isAdultTerm && isServiceTerm && isInformationTerm && !isMarketingTerm);
  };

  const handleServiceTermDescriptionClick = () => {
    setIsServiceTermDescription(!isServiceTermDescription);
    setIsInformationTermDescription(false);
    setIsMarketingTermDescription(false);
  };
  const handleInformationTermDescriptionClick = () => {
    setIsServiceTermDescription(false);
    setIsInformationTermDescription(!isInformationTermDescription);
    setIsMarketingTermDescription(false);
  };
  const handleMarketingTermDescriptionClick = () => {
    setIsServiceTermDescription(false);
    setIsInformationTermDescription(false);
    setIsMarketingTermDescription(!isMarketingTermDescription);
  };

  dispatch(authActions.checkSignupTerm(isAdultTerm && isServiceTerm && isInformationTerm));

  return {
    totalTerm: { checked: isTotalTerm, handler: handleTotalTermClick },
    adultTerm: { checked: isAdultTerm, handler: handleAdultTermClick },
    serviceTerm: {
      checked: isServiceTerm,
      handler: handleServiceTermClick,
      descriptionChecked: isServiceTermDescription,
      descriptionHandler: handleServiceTermDescriptionClick,
    },
    informationTerm: {
      checked: isInformationTerm,
      handler: handleInformationTermClick,
      descriptionChecked: isInformationTermDescription,
      descriptionHandler: handleInformationTermDescriptionClick,
    },
    marketingTerm: {
      checked: isMarketingTerm,
      handler: handleMarketingTermClick,
      descriptionChecked: isMarketingTermDescription,
      descriptionHandler: handleMarketingTermDescriptionClick,
    },
  };
};

export const useAuthInput = () => {
  const dispatch = useTypedDispatch();

  // email
  const email = useTypedSelector((state) => state.rootReducer.authReducer.email ?? '');
  const handleEmailChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(authActions.checkErrorCode(null));
    dispatch(authActions.writeEmail(ev.target.value));
  };

  // code
  const [code, setCode] = useState<string>('');
  const handleCodeChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(authActions.checkErrorCode(null));
    setCode(ev.target.value);
  };

  // username
  const [username, setUsername] = useState<string>('');
  const handleUsernameChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(authActions.checkErrorCode(null));
    setUsername(ev.target.value);
  };

  // password
  const [password, setPassword] = useState<string>('');
  const handlePasswordChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(authActions.checkErrorCode(null));
    setPassword(ev.target.value);
  };

  const [newPassword, setNewPassword] = useState<string>('');
  const handleNewPasswordChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(authActions.checkErrorCode(null));
    setNewPassword(ev.target.value);
  };

  const [currentPassword, setCurrentPassword] = useState<string>('');
  const handleCurrentPasswordChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(authActions.checkErrorCode(null));
    setCurrentPassword(ev.target.value);
  };

  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const handlePasswordVisibleClick = () => setPasswordVisible(!passwordVisible);

  // edit
  const [isPasswordEdit, setIsPasswordEdit] = useState<boolean>(false);
  const handlePasswordEditClick = () => setIsPasswordEdit(true);
  const handlePasswordCancelClick = () => {
    setCurrentPassword('');
    setNewPassword('');
    setIsPasswordEdit(false);
  };
  const handlePasswordCompleteClick = () => {
    setCurrentPassword('');
    setNewPassword('');
    setIsPasswordEdit(false);
  };

  return {
    email: { value: email, handler: handleEmailChange },
    code: { value: code, handler: handleCodeChange },
    username: { value: username, handler: handleUsernameChange },
    password: {
      value: password,
      handler: handlePasswordChange,
      visible: passwordVisible,
      visibleHandler: handlePasswordVisibleClick,
    },
    currentPassword: {
      value: currentPassword,
      handler: handleCurrentPasswordChange,
      visible: passwordVisible,
      visibleHandler: handlePasswordVisibleClick,
    },
    newPassword: {
      value: newPassword,
      handler: handleNewPasswordChange,
      visible: passwordVisible,
      visibleHandler: handlePasswordVisibleClick,
    },
    edit: {
      password: {
        isEdit: isPasswordEdit,
        editHandler: handlePasswordEditClick,
        cancelHandler: handlePasswordCancelClick,
        completeHandler: handlePasswordCompleteClick,
      },
    },
  };
};

/**
 * @description password가 유효한지 체크하는 hook
 */
const useAuthInputValidate = () => {
  const dispatch = useTypedDispatch();

  const validatePassword = ({
    password,
    currentPassword,
    newPassword,
  }: {
    password?: string;
    currentPassword?: string;
    newPassword?: string;
  }): boolean => {
    if (password) {
      const isPasswordValid = getIsValidPassword(password);
      if (!isPasswordValid) {
        dispatch(authActions.checkErrorCode('invalid-password-format'));
        return false;
      }
    } else {
      const isCurrentPasswordValid = getIsValidPassword(currentPassword ?? '');
      const isNewPasswordValid = getIsValidPassword(newPassword ?? '');
      if (!isCurrentPasswordValid || !isNewPasswordValid) {
        dispatch(authActions.checkErrorCode('invalid-password-format'));
        return false;
      }
    }

    return true;
  };

  const validateEmail = (email: string): boolean => {
    const isEmailValid = getIsValidEmail(email ?? null);
    if (!isEmailValid) {
      dispatch(authActions.checkErrorCode('invalid-email-format'));
      return false;
    }

    return true;
  };

  const validateUsername = (username: string): boolean => {
    const isUsernameValid = getIsValidUsername(username ?? null);
    if (!isUsernameValid) {
      dispatch(authActions.checkErrorCode('invalid-username-format'));
      return false;
    }

    return true;
  };

  const validateCode = (code: string): boolean => {
    const isCodeValid = getIsValidAuthenticationCode(code);

    if (!isCodeValid) {
      dispatch(authActions.checkErrorCode('invalid-authentication-code-format'));
      return false;
    }

    return true;
  };

  return { validatePassword, validateEmail, validateUsername, validateCode };
};

const useAuthInitialize = () => {
  const dispatch = useTypedDispatch();
  const { refetch, data } = useFindMeQuery();
  const navigate = useNavigate();
  const beforeSigninPage = useTypedSelector((state) => state.rootReducer.navigationReducer.beforeSigninPage);
  const { initializeStore } = useStoreInitialize();

  const authenticationInitialize = async ({
    userKey,
    accessToken,
    refreshToken,
    mode,
  }: {
    userKey: string;
    accessToken: string;
    refreshToken: string;
    mode: 'signin' | 'signup';
  }) => {
    const restorePath = initializeStore();
    dispatch(userActions.authenticate({ userKey, accessToken, refreshToken }));
    // 가입/로그인 환영 모달 표시 (mode props로 카피 분기)
    // dispatch(modalActions.showDialogModal({ type: 'auth-complete', data: { mode } }));
    await refetch();

    // signin한뒤, mainMenu/subMenu 기반 경로로 라우팅 (cloze 같은 sub-page는 무시, 안전한 메뉴 페이지로 복귀)
    // 서비스 분기는 추후 다른 정책 적용 가능성 위해 유지 (현재는 동일 동작)
    try {
      if (SERVICE === EService.IYAGIGO) {
        navigate(restorePath, { replace: true });
      } else if (SERVICE === EService.GRIDGIO) {
        navigate(getInitialServicePath(), { replace: true });
      } else {
        const url = new URL(beforeSigninPage);
        navigate(url.pathname + url.search, { replace: true });
      }
    } catch {
      navigate(restorePath, { replace: true });
    }
    dispatch(navigationActions.removeBeforeSigninPage());
  };

  const unauthenticationInitialize = () => {
    // dispatch(authActions.initialize());
    initializeStore();
    dispatch(userActions.unAuthenticate());

    setTimeout(() => {
      window.location.href = getInitialServicePath();
    }, 0);
  };

  return { authenticationInitialize, unauthenticationInitialize };
};

/**
 * signin & signout hook (inhouse/oauth)
 */
export const useSignUpAndIn = () => {
  const dispatch = useTypedDispatch();
  const oauthSignupMutation = useOAuthSignupMutation();
  const openOAuthSignInMutation = useOpenOAuthSignInPageMutation();
  const inhouseSignupMutation = useInhouseSignupMutation();
  const inhouseSigninMutation = useInhouseSigninMutation();
  const { validatePassword, validateUsername } = useAuthInputValidate();
  const { authenticationInitialize } = useAuthInitialize();

  // 가입/로그인 버튼 중복 클릭 방지.
  // 버튼이 div(TextBlock)라 disabled가 없고 응답 전까지 아무 피드백이 없어 연타가 쉽다.
  // 연타하면 서버의 check-then-insert가 동시 요청을 막지 못해 user 행이 2건 생성된다.
  const isSigning =
    oauthSignupMutation.isPending ||
    inhouseSignupMutation.isPending ||
    inhouseSigninMutation.isPending ||
    openOAuthSignInMutation.isPending;

  const oauthSignup = async (
    authType: Exclude<EAuthType, 'inhouse'>,
    username: string,
    email: string,
    isMarketingTerm: boolean,
  ): Promise<void> => {
    try {
      if (isSigning) return;
      if (!validateUsername(username)) return;
      const data = await oauthSignupMutation
        .mutateAsync({
          service: SERVICE,
          authType: authType!,
          username,
          email: email,
          marketingConsent: isMarketingTerm,
          studentKey:
            SERVICE === EService.IYAGIGO ? (localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? undefined) : undefined,
        })
        .then((res) => res.data);
      const { key, accessToken, refreshToken } = data;
      await authenticationInitialize({ userKey: key, accessToken, refreshToken, mode: 'signup' });
    } catch (err) {
      return;
    }
  };

  const inhouseSignup = async (
    username: string,
    email: string,
    password: string,
    isMarketingTerm: boolean,
  ): Promise<void> => {
    try {
      if (isSigning) return;
      if (!validateUsername(username)) return;
      if (!validatePassword({ password })) return;
      const data = await inhouseSignupMutation
        .mutateAsync({
          service: SERVICE,
          username,
          password,
          email,
          marketingConsent: isMarketingTerm,
          studentKey:
            SERVICE === EService.IYAGIGO ? (localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? undefined) : undefined,
        })
        .then((res) => res.data);

      const { key, accessToken, refreshToken } = data;
      await authenticationInitialize({ userKey: key, accessToken, refreshToken, mode: 'signup' });
    } catch (err) {
      return;
    }
  };

  const inhouseSignin = async (service: EService, username: string, password: string) => {
    try {
      if (isSigning) return;
      if (!validateUsername(username)) return;
      if (!validatePassword({ password })) return;

      const data = await inhouseSigninMutation
        .mutateAsync({
          service,
          username,
          password,
        })
        .then((res) => res.data);

      const { key, accessToken, refreshToken } = data;
      await authenticationInitialize({ userKey: key, accessToken, refreshToken, mode: 'signin' });
    } catch (err) {
      return;
    }
  };

  const oauthPage = async (authType: Exclude<EAuthType, 'inhouse'>): Promise<void> => {
    try {
      if (isSigning) return;
      const response = await openOAuthSignInMutation.mutateAsync({
        service: SERVICE,
        type: authType!,
        kind: 'signin',
      });
      const oauthPageUrl = response?.data?.oauthPageUrl;
      dispatch(authActions.initialize());
      if (!oauthPageUrl) return;
      window.location.href = oauthPageUrl;
    } catch (err) {
      return;
    }
  };

  const oauthSignin = async (userKey: string, accessToken: string, refreshToken: string): Promise<void> => {
    await authenticationInitialize({ userKey, accessToken, refreshToken, mode: 'signin' });
  };

  return { oauthPage, oauthSignup, oauthSignin, inhouseSignup, inhouseSignin, isSigning };
};

/**
 * 로그아웃 및 회원탈퇴 hoot
 */
export const useSignout = () => {
  const dispatch = useTypedDispatch();
  const removeMeMutation = useRemoveMeMutation();
  const signoutMutation = useSignoutMutation();
  const { unauthenticationInitialize } = useAuthInitialize();

  const removeMe = async (): Promise<boolean> => {
    try {
      await removeMeMutation.mutateAsync().then((res) => res.data);
      unauthenticationInitialize();
      // dispatch(authActions.initialize()); <- 탈퇴알림을 보여주기 위해서
      return true;
    } catch (err) {
      return false;
    }
  };

  const signout = async () => {
    try {
      await signoutMutation.mutateAsync();
    } catch (err) {
      return;
    } finally {
      unauthenticationInitialize();
    }
  };

  return { signout, removeMe };
};

/**
 * 비밀번호 hook
 */
export const usePassword = () => {
  const dispatch = useTypedDispatch();
  const modifyMeMutation = useModifyMeMutation();
  const { validatePassword } = useAuthInputValidate();

  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    try {
      if (!validatePassword({ currentPassword, newPassword })) return false;
      const data = await modifyMeMutation
        .mutateAsync({
          currentPassword,
          newPassword,
        })
        .then((res) => res.data);
      return true;
    } catch {
      return false;
    }
  };

  const repasswordMutation = useRepasswordMutation();

  const repassword = async (username: string, password: string): Promise<boolean> => {
    try {
      if (!validatePassword({ password })) return false;
      await repasswordMutation.mutateAsync({ service: SERVICE, username: username, password });
      return true;
    } catch (err) {
      return false;
    }
  };

  return { changePassword, repassword };
};

export const useAuthenticationCode = () => {
  const dispatch = useTypedDispatch();
  const authenticateCodeMutation = useAuthenticateCodeMutation();
  const { validateUsername, validateEmail, validateCode } = useAuthInputValidate();
  const issueCodeMutation = useIssueCodeMutation();

  type TCodeStep = 'init' | 'sent' | 'authenticated';
  const [codeState, setCodeState] = useState<TCodeStep>('init');

  // 인증코드 발송/검증 버튼 중복 클릭 방지.
  // issueCode도 서버에서 조회 후 없으면 INSERT하는 구조라 동시 요청이 authentication 행을 2건 만든다.
  const isAuthenticating = issueCodeMutation.isPending || authenticateCodeMutation.isPending;

  const issueAuthenticationCode = async ({
    service,
    email,
    username,
  }: {
    service: EService;
    email?: string;
    username?: string;
  }): Promise<string | void> => {
    try {
      if (isAuthenticating) return;

      let received;

      if (username) {
        if (!validateUsername(username)) return;
        const data = await issueCodeMutation.mutateAsync({ service, username: username, type: 'email' });
        received = data.data.email;
      }

      if (email) {
        if (!validateEmail(email)) return;
        const data = await issueCodeMutation.mutateAsync({ service, email: email, type: 'email' });
        received = data.data.email;
      }

      setCodeState('sent');

      return received;
    } catch (err) {
      return;
    }
  };

  const authenticateCode = async (email: string, code: string): Promise<boolean> => {
    try {
      if (isAuthenticating) return false;
      if (!validateCode(code)) return false;
      await authenticateCodeMutation.mutateAsync({ email: email, code: code });
      setCodeState('authenticated');
      return true;
    } catch (err) {
      return false;
    }
  };

  return { issueAuthenticationCode, authenticateCode, codeState, isAuthenticating };
};

export const useAuthPage = () => {
  const dispatch = useTypedDispatch();

  const openSignupPage = (authType: EAuthType, email: string) =>
    dispatch(authActions.openSignupPage({ authType, email }));

  const openAuthPage = (page: 'signout' | 'findid' | 'findpw' | 'mypage' | 'signin' | 'code') => {
    switch (page) {
      case 'signout':
        dispatch(authActions.openSignoutPage());
        break;
      case 'findid':
        dispatch(authActions.openFindIdPage());
        break;
      case 'findpw':
        dispatch(authActions.openFindPwPage());
        break;
      case 'mypage':
        dispatch(authActions.openMyPage());
        break;
      case 'signin':
        dispatch(navigationActions.storeBeforeSigninPage(window.location.href));
        dispatch(authActions.openSigninPage());
        break;
      case 'code':
        dispatch(authActions.openAuthCodePage());
        break;
      default:
        break;
    }
  };

  return { openSignupPage, openAuthPage };
};

export const useFindMe = () => {
  const { refetch, data } = useFindMeQuery();
  const isAuthenticated = useTypedSelector((state) => state.rootReducer.userReducer.isAuthenticated);

  useEffect(() => {
    (async () => {
      if (!isAuthenticated) return;
      await refetch();
    })();
  }, []);

  return data;
};
